import { Layout } from "@/components/layout/Layout";
import { Link } from "wouter";
import { ChevronRight, Search, Filter, MapPin, X, Loader2, Calendar, Home, DollarSign, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger 
} from "@/components/ui/sheet";
import { useQuery } from "@tanstack/react-query";
import { DayPicker, DateRange } from "react-day-picker";
import { format, addDays, isAfter, isBefore, isWithinInterval, eachDayOfInterval } from "date-fns";
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from "@/components/ui/popover";
import "react-day-picker/dist/style.css";
import { apiRequest } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DUBAI_AREAS, AREA_CODE_MAP, AREA_NAME_MAP } from '@shared/areas';
import { useAuth } from '@/hooks/use-auth';

// Define the property interface
interface PropertyImage {
  id: number;
  imageUrl: string;
  isPrimary: boolean;
}

interface Property {
  id: number;
  title: string;
  description: string;
  area: string;
  address: string;
  basePrice?: number;  // Now optional since it may be hidden for non-link users
  totalPrice?: number; // For calculated prices with markup
  bedrooms: number;
  bathrooms: number;
  maxGuests: number;
  features: string[] | null;
  images?: PropertyImage[];
  primaryImage?: string;
  featured?: boolean;
}

// Price Range Slider Component
function PriceRangeSlider({ 
  minPrice, 
  maxPrice, 
  value, 
  onChange, 
  step = 50 
}: { 
  minPrice: number; 
  maxPrice: number; 
  value: [number, number]; 
  onChange: (value: [number, number]) => void; 
  step?: number;
}) {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);

  // Update local value when prop changes (but not during dragging)
  useEffect(() => {
    if (!isDragging) {
      setLocalValue(value);
    }
  }, [value, isDragging]);

  // Only call onChange when dragging stops
  const handleDragEnd = useCallback((newValue: [number, number]) => {
    onChange(newValue);
  }, [onChange]);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = Math.min(Number(e.target.value), localValue[1] - step);
    const newValue: [number, number] = [newMin, localValue[1]];
    setLocalValue(newValue);
    // Don't call onChange while dragging - only when released
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = Math.max(Number(e.target.value), localValue[0] + step);
    const newValue: [number, number] = [localValue[0], newMax];
    setLocalValue(newValue);
    // Don't call onChange while dragging - only when released
  };

  const handleMinMouseDown = () => {
    setIsDragging(true);
  };

  const handleMinMouseUp = () => {
    setIsDragging(false);
    handleDragEnd(localValue);
  };

  const handleMaxMouseDown = () => {
    setIsDragging(true);
  };

  const handleMaxMouseUp = () => {
    setIsDragging(false);
    handleDragEnd(localValue);
  };

  const handleInputChange = (index: 0 | 1, inputValue: string) => {
    const numValue = parseInt(inputValue) || 0;
    let newValue: [number, number];
    
    if (index === 0) {
      newValue = [Math.max(minPrice, Math.min(numValue, localValue[1] - step)), localValue[1]];
    } else {
      newValue = [localValue[0], Math.min(maxPrice, Math.max(numValue, localValue[0] + step))];
    }
    
    setLocalValue(newValue);
    // For input fields, call onChange immediately since user finished typing
    onChange(newValue);
  };

  // Calculate the percentage positions for the visual range
  const minPercent = ((localValue[0] - minPrice) / (maxPrice - minPrice)) * 100;
  const maxPercent = ((localValue[1] - minPrice) / (maxPrice - minPrice)) * 100;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between text-sm font-medium">
        <span>AED {localValue[0]}</span>
        <span>AED {localValue[1]}</span>
      </div>
      
      <div className="relative h-6">
        {/* Track background */}
        <div className="absolute top-1/2 transform -translate-y-1/2 w-full h-2 bg-gray-200 rounded-lg"></div>
        
        {/* Active range highlight */}
        <div 
          className="absolute top-1/2 transform -translate-y-1/2 h-2 bg-primary rounded-lg"
          style={{
            left: `${minPercent}%`,
            width: `${maxPercent - minPercent}%`
          }}
        ></div>
        
        {/* Min range input */}
        <input
          type="range"
          min={minPrice}
          max={maxPrice}
          step={step}
          value={localValue[0]}
          onChange={(e) => handleInputChange(0, e.target.value)}
          onMouseDown={handleMinMouseDown}
          onMouseUp={handleMinMouseUp}
          onTouchStart={handleMinMouseDown}
          onTouchEnd={handleMinMouseUp}
          aria-label={`Minimum price: AED ${localValue[0]}`}
          className="absolute w-full h-6 bg-transparent appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 rounded-lg slider-input"
          style={{
            background: 'transparent',
            WebkitAppearance: 'none',
            MozAppearance: 'none'
          }}
        />
        
        {/* Max range input */}
        <input
          type="range"
          min={minPrice}
          max={maxPrice}
          step={step}
          value={localValue[1]}
          onChange={(e) => handleInputChange(1, e.target.value)}
          onMouseDown={handleMaxMouseDown}
          onMouseUp={handleMaxMouseUp}
          onTouchStart={handleMaxMouseDown}
          onTouchEnd={handleMaxMouseUp}
          aria-label={`Maximum price: AED ${localValue[1]}`}
          className="absolute w-full h-6 bg-transparent appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 rounded-lg slider-input"
          style={{
            background: 'transparent',
            WebkitAppearance: 'none',
            MozAppearance: 'none'
          }}
        />
      </div>
      
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Min Price</label>
          <Input
            type="number"
            value={localValue[0]}
            onChange={(e) => handleInputChange(0, e.target.value)}
            className="h-8 text-xs"
            min={minPrice}
            max={localValue[1] - step}
            step={step}
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Max Price</label>
          <Input
            type="number"
            value={localValue[1]}
            onChange={(e) => handleInputChange(1, e.target.value)}
            className="h-8 text-xs"
            min={localValue[0] + step}
            max={maxPrice}
            step={step}
          />
        </div>
      </div>
      
      <style>{`
        .slider-input::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #ffffff;
          border: 2px solid hsl(var(--primary));
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          position: relative;
          z-index: 2;
        }
        
        .slider-input::-webkit-slider-thumb:hover {
          background: hsl(var(--primary));
        }
        
        .slider-input::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #ffffff;
          border: 2px solid hsl(var(--primary));
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          position: relative;
          z-index: 2;
          -moz-appearance: none;
          appearance: none;
        }
        
        .slider-input::-moz-range-thumb:hover {
          background: hsl(var(--primary));
        }
        
        .slider-input::-webkit-slider-track {
          background: transparent;
        }
        
        .slider-input::-moz-range-track {
          background: transparent;
          border: none;
        }
      `}</style>
    </div>
  );
}

// Generic image carousel for property images (mobile & desktop)
export function PropertyImageCarousel({ images, title }: { images: any[]; title: string }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState<{ [key: string]: boolean }>({});
  const startPosRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const carouselId = useRef(`carousel-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`); // More unique ID

  // Initialize loading states for all images with unique keys
  useEffect(() => {
    if (images && images.length > 0) {
      const initialLoadingStates: { [key: string]: boolean } = {};
      images.forEach((_, index) => {
        const uniqueKey = `${carouselId.current}-${index}`;
        // Start with false - only set to true when we actually start loading
        initialLoadingStates[uniqueKey] = false;
      });
      setImageLoadingStates(initialLoadingStates);
    }
  }, [images]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      // Clean up any object URLs that might have been created
      Object.keys(imageLoadingStates).forEach(key => {
        // This cleanup is for any potential memory leaks
      });
    };
  }, []);

  // Preload image at a given index
  const preloadImage = (index: number) => {
    if (!images || images.length === 0) return;
    const img = new window.Image();
    img.src = images[index].imageUrl;
  };

  // Preload next and previous images for smoother experience
  useEffect(() => {
    if (!images || images.length === 0) return;
    preloadImage((currentIndex + 1) % images.length);
    preloadImage((currentIndex - 1 + images.length) % images.length);
  }, [currentIndex, images]);

  // Handle swipe with preloading and loading state
  const goToIndex = (newIndex: number) => {
    if (!images || images.length === 0) return;
    setCurrentIndex(newIndex);
    setDragOffset(0);
  };

  // Handle image loading states with unique keys
  const handleImageLoadStart = (index: number) => {
    const uniqueKey = `${carouselId.current}-${index}`;
    setImageLoadingStates(prev => ({ ...prev, [uniqueKey]: true }));
  };

  const handleImageLoad = (index: number) => {
    const uniqueKey = `${carouselId.current}-${index}`;
    setImageLoadingStates(prev => ({ ...prev, [uniqueKey]: false }));
  };

  const handleImageError = (index: number) => {
    const uniqueKey = `${carouselId.current}-${index}`;
    console.log(`Image error for ${uniqueKey}`);
    setImageLoadingStates(prev => ({ ...prev, [uniqueKey]: false }));
  };

  // Touch event handlers for smooth dragging
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!images || images.length <= 1) return;
    
    // Always prevent default on image touch to prioritize carousel over page scroll
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(true);
    setDragOffset(0);
    startPosRef.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !images || images.length <= 1) return;
    
    // Always prevent default when dragging images - image navigation takes priority
    e.preventDefault();
    e.stopPropagation();
    
    const currentX = e.touches[0].clientX;
    const deltaX = currentX - startPosRef.current;
    const containerWidth = e.currentTarget.getBoundingClientRect().width;
    
    // Convert to percentage and limit range
    const offsetPercent = (deltaX / containerWidth) * 100;
    const clampedOffset = Math.max(-50, Math.min(50, offsetPercent));
    
    setDragOffset(clampedOffset);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging || !images || images.length <= 1) return;
    
    // Always prevent default to avoid any unwanted page behavior
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(false);
    
    // Lower threshold for easier navigation - just 5% swipe needed
    if (Math.abs(dragOffset) > 5) {
      if (dragOffset < 0) {
        // Dragged left - next image
        setCurrentIndex((prev) => (prev + 1) % images.length);
      } else {
        // Dragged right - previous image
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
      }
    }
    
    // Always reset offset
    setDragOffset(0);
  };

  // Mouse event handlers for desktop
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!images || images.length <= 1) return;
    setIsDragging(true);
    setDragOffset(0);
    startPosRef.current = e.clientX;
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !images || images.length <= 1) return;
    
    const currentX = e.clientX;
    const deltaX = currentX - startPosRef.current;
    const containerWidth = e.currentTarget.getBoundingClientRect().width;
    
    const offsetPercent = (deltaX / containerWidth) * 100;
    const clampedOffset = Math.max(-50, Math.min(50, offsetPercent));
    
    setDragOffset(clampedOffset);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Determine if we should change image (reduced threshold for smoother experience: 10%)
    if (Math.abs(dragOffset) > 10) {
      if (dragOffset < 0) {
        // Dragged left - next image
        setCurrentIndex((prev) => (prev + 1) % images.length);
      } else {
        // Dragged right - previous image
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
      }
    }
    
    // Always reset offset
    setDragOffset(0);
  };

  if (!images || images.length === 0) {
    return (
      <div className="w-full h-full bg-muted flex items-center justify-center">
        <p className="text-muted-foreground">No image available</p>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full overflow-hidden cursor-grab active:cursor-grabbing select-none"
      style={{ 
        touchAction: 'none', // Completely disable touch gestures to prioritize carousel
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none'
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {images.map((image, index) => {
        const uniqueKey = `${carouselId.current}-${index}`;
        const isImageLoading = imageLoadingStates[uniqueKey] === true;
        
        return (
          <div
            key={index}
            className={`absolute inset-0 transition-transform duration-300 ease-out ${isDragging ? 'duration-0' : ''}`}
            style={{
              transform: `translateX(${(index - currentIndex) * 100 + dragOffset}%)`,
            }}
          >
            {/* Only show grey background when explicitly loading AND image exists */}
            {isImageLoading && image?.imageUrl && (
              <div className="absolute inset-0 bg-gray-200 flex items-center justify-center z-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            )}
            
            {/* Image */}
            {image?.imageUrl ? (
              <img
                src={image.imageUrl}
                alt={`${title} - Image ${index + 1}`}
                className="w-full h-full object-cover"
                loading={index === 0 ? "eager" : "lazy"}
                onLoadStart={() => {
                  // Only show loading state if image is not already complete/cached
                  const imgElement = document.querySelector(`img[src="${image.imageUrl}"]`) as HTMLImageElement;
                  if (!imgElement || !imgElement.complete) {
                    handleImageLoadStart(index);
                  }
                }}
                onLoad={() => handleImageLoad(index)}
                onError={() => handleImageError(index)}
              />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <p className="text-muted-foreground text-sm">Image not available</p>
              </div>
            )}
          </div>
        );
      })}
      {images.length > 1 && (
        <button
          type="button"
          aria-label="Next image"
          onClick={e => {
            e.stopPropagation();
            goToIndex((currentIndex + 1) % images.length);
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/0 p-1 rounded-full z-10"
          style={{ opacity: 0.8 }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      )}
    </div>
  );
}

// Add prop type for linkCode
interface PropertiesPageProps {
  linkCode?: string;
}

export default function PropertiesPage({ linkCode }: PropertiesPageProps) {
  const { user, isLoading: isAuthLoading } = useAuth();
  console.log('User:', user);

  // Determine if user can see prices and thus use price sorting
  const canSeePrices = !!linkCode || (user && ['admin', 'manager', 'agent'].includes(user.role?.toLowerCase()));

  // Place all useState, useRef, and variable declarations here
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [bedrooms, setBedrooms] = useState<number | null>(null);
  const [checkInDate, setCheckInDate] = useState<Date | undefined>(undefined);
  const [checkOutDate, setCheckOutDate] = useState<Date | undefined>(undefined);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [mobileDateModalOpen, setMobileDateModalOpen] = useState(false);
  const [pageSize, setPageSize] = useState(12);
  const [page, setPage] = useState(1);
  const [propertyList, setPropertyList] = useState<Property[]>([]);
  const [totalProperties, setTotalProperties] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [isLoadingProperties, setIsLoadingProperties] = useState(false);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [sortBy, setSortBy] = useState<'featured' | 'newest' | 'price-low' | 'price-high'>('newest');
  
  // Price range state
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 5000]);
  const [priceRangeEnabled, setPriceRangeEnabled] = useState(false);

  // Fetch properties depending on linkCode (now only set for /properties/link/:linkCode)
  const { data: propertiesData, isLoading, isFetching, error } = useQuery({
    queryKey: linkCode ? ["property-link-properties", linkCode] : ["public-properties"],
    queryFn: async () => {
      if (linkCode) {
        // Store linkCode in sessionStorage for downstream API requests (like in link.tsx)
        sessionStorage.setItem("staymiddle_link_code", linkCode);
        const res = await apiRequest("GET", `property-links/${linkCode}`);
        // If it's a single property, wrap in array for uniformity
        if (res.properties) return res.properties;
        if (res.property) return [res.property];
        return [];
      } else {
        // Remove link code from sessionStorage if not in link mode
        sessionStorage.removeItem("staymiddle_link_code");
        return await apiRequest("GET", "public-properties");
      }
    },
    enabled: !isAuthLoading,
  });

  // Fetch property availability dates
  const { data: availabilityData } = useQuery({
    queryKey: ['property-availability'],
    queryFn: async () => {
      // This endpoint would return property availabilities
      // For now, we'll just implement the UI with date selection
      return {};
    },
    enabled: false, // Only fetch when explicitly needed
  });

  // Get all properties
  const properties: Property[] = Array.isArray(propertiesData) ? propertiesData : [];
  
  // Calculate price range bounds from available properties
  const priceRangeBounds = useMemo(() => {
    if (properties.length === 0) return { min: 0, max: 5000 };
    
    const prices = properties
      .map(p => p.totalPrice || p.basePrice)
      .filter(price => price !== undefined) as number[];
    
    if (prices.length === 0) return { min: 0, max: 5000 };
    
    const min = Math.floor(Math.min(...prices) / 50) * 50; // Round down to nearest 50
    const max = Math.ceil(Math.max(...prices) / 50) * 50;  // Round up to nearest 50
    
    return { min: Math.max(0, min), max: Math.max(1000, max) };
  }, [properties]);

  // Update price range when bounds change (only if not manually set)
  useEffect(() => {
    if (!priceRangeEnabled && properties.length > 0) {
      setPriceRange([priceRangeBounds.min, priceRangeBounds.max]);
    }
  }, [priceRangeBounds, priceRangeEnabled, properties.length]);

  // For the locations dropdown, use area codes as values and area names as labels
  const locations = DUBAI_AREAS.map(areaName => ({ label: areaName, value: AREA_CODE_MAP[areaName] }));

  // Format date for display
  const formatDateString = (date: Date | undefined) => {
    if (!date) return '';
    return format(date, 'MMM dd, yyyy');
  };

  // Helper function to normalize dates to avoid timezone issues
  const normalizeDate = (date: Date) => {
    // Create a new date at midnight in local timezone
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateForUrl = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get today's date normalized for consistent comparison
  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  // Update handleDateSelection to close the popover when both dates are selected
  const handleDateSelection = (range: DateRange | undefined) => {
    console.log('🔍 Raw date selection input:', {
      hasRange: !!range,
      from: range?.from,
      to: range?.to,
      fromStr: range?.from ? formatDateForUrl(range.from) : undefined,
      toStr: range?.to ? formatDateForUrl(range.to) : undefined
    });
    
    if (!range) {
      console.log('🗑️ Date selection cleared');
      setCheckInDate(undefined);
      setCheckOutDate(undefined);
      return;
    }
    
    // Normalize dates to avoid timezone issues
    const normalizedFrom = range.from ? normalizeDate(range.from) : undefined;
    const normalizedTo = range.to ? normalizeDate(range.to) : undefined;
    
    const fromStr = normalizedFrom ? formatDateForUrl(normalizedFrom) : undefined;
    const toStr = normalizedTo ? formatDateForUrl(normalizedTo) : undefined;
    
    console.log('📅 Date selection changed:', fromStr, 'to', toStr);
    console.log('🔧 Comparison - Original vs Normalized:', {
      originalFrom: range?.from ? formatDateForUrl(range.from) : undefined,
      normalizedFrom: fromStr,
      originalTo: range?.to ? formatDateForUrl(range.to) : undefined,
      normalizedTo: toStr
    });
    
    setCheckInDate(normalizedFrom);
    setCheckOutDate(normalizedTo);
    
    if (normalizedFrom && normalizedTo) {
      setCalendarOpen(false);
      console.log('✅ Calendar closed with selection:', fromStr, 'to', toStr);
    }
  };

  // Features list (should match property features)
  const allFeatures = [
    "Pool",
    "Gym",
    "Parking",
    "Beach Access",
    "Scenic View"
  ];

  // Handle feature checkbox change
  const handleFeatureChange = (feature: string, checked: boolean) => {
    setSelectedFeatures((prev) =>
      checked ? [...prev, feature] : prev.filter((f) => f !== feature)
    );
  };

  // Fetch paginated properties
  const fetchProperties = useCallback(async (reset = false) => {
    if (reset) setIsLoadingProperties(true); // Only for full reload
    try {
      let url = linkCode
        ? `property-links/${linkCode}?page=${reset ? 1 : page}&pageSize=${pageSize}`
        : `public-properties?page=${reset ? 1 : page}&pageSize=${pageSize}`;
      // Add filters/search params as needed
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      if (selectedLocation && selectedLocation !== 'any') {
        params.set('location', selectedLocation);
      }
      if (bedrooms !== null) params.set('bedrooms', bedrooms.toString());
      if (featuredOnly) params.set('featured', 'true');
      if (sortBy !== 'newest') params.set('sort', sortBy);
      
      // Add price range parameters if enabled and user can see prices
      if (priceRangeEnabled && canSeePrices) {
        params.set('minPrice', priceRange[0].toString());
        params.set('maxPrice', priceRange[1].toString());
      }
      
      // Add availability dates ONLY when BOTH check-in and check-out dates are selected
      if (checkInDate && checkOutDate) {
        const startDateStr = formatDateForUrl(checkInDate);
        const endDateStr = formatDateForUrl(checkOutDate);
        params.set('startDate', startDateStr);
        params.set('endDate', endDateStr);
        console.log('🚀 Fetching properties with date filter:', {
          checkInDate: startDateStr,
          checkOutDate: endDateStr,
          originalCheckInDate: checkInDate,
          originalCheckOutDate: checkOutDate
        });
      } else if (checkInDate || checkOutDate) {
        console.log('⏳ Incomplete date selection - waiting for both dates before applying filter:', {
          checkInDate: checkInDate ? formatDateForUrl(checkInDate) : null,
          checkOutDate: checkOutDate ? formatDateForUrl(checkOutDate) : null
        });
      }
      
      url += `&${params.toString()}`;
      const res = await apiRequest("GET", url);
      const newData = Array.isArray(res.data) ? res.data : Array.isArray(res) ? res : res.properties || res.property ? (res.properties || [res.property]) : [];
      setTotalProperties(res.total || newData.length);
      setPropertyList(prev => reset
        ? (newData as Property[]).map((p: Property) => ({ ...p, featured: !!p.featured }))
        : [...prev, ...(newData as Property[]).map((p: Property) => ({ ...p, featured: !!p.featured }))]
      );
    } finally {
      if (reset) setIsLoadingProperties(false); // Only for full reload
    }
  }, [linkCode, page, pageSize, searchTerm, selectedLocation, bedrooms, featuredOnly, sortBy, priceRange, priceRangeEnabled, canSeePrices, checkInDate, checkOutDate]);

  // Apply frontend-only filters to the backend-paginated results
  const displayProperties = useMemo(() => {
    return propertyList.filter((property) => {
      // Filter by features (frontend only) - all other filters now handled on backend
      const matchesFeatures =
        selectedFeatures.length === 0 ||
        (property.features &&
          selectedFeatures.every((feature) =>
            property.features?.map((f) => f.toLowerCase()).includes(feature.toLowerCase())
          ));
      return matchesFeatures;
    });
  }, [propertyList, selectedFeatures]);

  // Calculate display total (accounts for frontend filtering) 
  const displayTotal = useMemo(() => {
    // If we have frontend-only filters active, use filtered count
    const hasFrontendFilters = selectedFeatures.length > 0;
    
    if (hasFrontendFilters) {
      return displayProperties.length;
    }
    
    // Otherwise use backend total (which now includes all backend filtering including availability)
    return totalProperties;
  }, [displayProperties.length, totalProperties, selectedFeatures.length]);

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedLocation(null);
    setBedrooms(null);
    setCheckInDate(undefined);
    setCheckOutDate(undefined);
    setSelectedFeatures([]);
    setPriceRangeEnabled(false);
    setPriceRange([priceRangeBounds.min, priceRangeBounds.max]);
  };
  
  function isMobile() {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  }

  // Initial and filter/search change load
  useEffect(() => {
    if (isAuthLoading) return;
    
    console.log('fetchProperties effect triggered - all filters handled on backend');
    setPage(1);
    setPropertyList([]);
    setIsLoadingProperties(true);
    fetchProperties(true).finally(() => setIsLoadingProperties(false));
    // eslint-disable-next-line
  }, [linkCode, searchTerm, selectedLocation, bedrooms, selectedFeatures, checkInDate, checkOutDate, isAuthLoading, featuredOnly, sortBy, priceRange, priceRangeEnabled]);

  // Load more when page changes (but not on first load)
  useEffect(() => {
    if (page === 1) return;
    setIsLoadingMore(true);
    fetchProperties().finally(() => setIsLoadingMore(false));
    // eslint-disable-next-line
  }, [page]);

  // Infinite scroll observer
  useEffect(() => {
    if (!loadMoreRef.current) return;
    
    const observer = new window.IntersectionObserver((entries) => {
      const [entry] = entries;
      
      // Debug logging
      console.log('Intersection triggered:', {
        isIntersecting: entry.isIntersecting,
        propertyListLength: propertyList.length,
        totalProperties: totalProperties,
        isLoadingMore: isLoadingMore
      });
      
      if (entry.isIntersecting && propertyList.length < totalProperties && !isLoadingMore) {
        console.log('Loading more properties...');
        setPage(p => p + 1);
      }
    }, { 
      threshold: 0.1, // Trigger when 10% of the element is visible
      rootMargin: '50px' // Trigger 50px before the element comes into view
    });
    
    observer.observe(loadMoreRef.current);
    
    return () => observer.disconnect();
  }, [propertyList.length < totalProperties && !isLoadingMore]); // Simplified dependency

  // DEBUG: Print loading states
  console.log('isLoading:', isLoading, 'isLoadingProperties:', isLoadingProperties, 'isFetching:', isFetching, 'propertyList.length :', propertyList.length, 'totalProperties:', totalProperties);

  // 1. On mount, initialize state from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setSearchTerm(params.get('search') ?? '');
    setSelectedLocation(params.get('location') ?? '');
    setBedrooms(params.get('bedrooms') ? parseInt(params.get('bedrooms')!) : null);
    
    // Parse dates in YYYY-MM-DD format
    const checkIn = params.get('checkIn');
    if (checkIn && /^\d{4}-\d{2}-\d{2}$/.test(checkIn)) {
      const [year, month, day] = checkIn.split('-').map(Number);
      const parsedCheckIn = new Date(year, month - 1, day); // month is 0-indexed
      console.log('✅ Parsed checkIn from URL:', checkIn, '→', formatDateForUrl(parsedCheckIn));
      setCheckInDate(parsedCheckIn);
    }
    const checkOut = params.get('checkOut');
    if (checkOut && /^\d{4}-\d{2}-\d{2}$/.test(checkOut)) {
      const [year, month, day] = checkOut.split('-').map(Number);
      const parsedCheckOut = new Date(year, month - 1, day); // month is 0-indexed
      console.log('✅ Parsed checkOut from URL:', checkOut, '→', formatDateForUrl(parsedCheckOut));
      setCheckOutDate(parsedCheckOut);
    }
    
    // Parse price range
    const minPrice = params.get('minPrice');
    const maxPrice = params.get('maxPrice');
    if (minPrice && maxPrice) {
      setPriceRange([parseInt(minPrice), parseInt(maxPrice)]);
      setPriceRangeEnabled(true);
    }
    
    const features = params.get('features');
    setSelectedFeatures(features ? features.split(',') : []);
    setPage(params.get('page') ? parseInt(params.get('page')!) : 1);
    setPageSize(params.get('pageSize') ? parseInt(params.get('pageSize')!) : 12);
    setFeaturedOnly(params.get('featured') === 'true');
    setSortBy(params.get('sort') === 'featured' ? 'featured' : 
              params.get('sort') === 'price-low' ? 'price-low' :
              params.get('sort') === 'price-high' ? 'price-high' : 'newest');
    
    // Fix malformed URLs (e.g., duplicate link codes)
    if (linkCode) {
      const currentPath = window.location.pathname;
      const expectedPath = `/properties/link/${linkCode}`;
      
      // If the current path doesn't match the expected path, fix it
      if (currentPath !== expectedPath) {
        const newUrl = expectedPath + window.location.search;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [linkCode]);

  // 2. Sync state to URL on change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (selectedLocation && selectedLocation !== 'any') params.set('location', selectedLocation);
    if (bedrooms !== null) params.set('bedrooms', bedrooms.toString());
    
    // Add availability dates to URL in YYYY-MM-DD format (timezone-safe)
    // Only add dates to URL when BOTH are selected to avoid confusion
    if (checkInDate && checkOutDate) {
      const checkInStr = formatDateForUrl(checkInDate);
      const checkOutStr = formatDateForUrl(checkOutDate);
      params.set('checkIn', checkInStr);
      params.set('checkOut', checkOutStr);
      console.log('🔗 Adding complete date range to URL:', checkInStr, 'to', checkOutStr);
    } else {
      console.log('🔗 Skipping URL date params - incomplete selection:', {
        checkInDate: checkInDate ? formatDateForUrl(checkInDate) : null,
        checkOutDate: checkOutDate ? formatDateForUrl(checkOutDate) : null
      });
    }
    
    // Add price range to URL
    if (priceRangeEnabled) {
      params.set('minPrice', priceRange[0].toString());
      params.set('maxPrice', priceRange[1].toString());
    }
    
    if (selectedFeatures.length > 0) params.set('features', selectedFeatures.join(','));
    if (page !== 1) params.set('page', page.toString());
    if (pageSize !== 12) params.set('pageSize', pageSize.toString());
    if (featuredOnly) params.set('featured', 'true');
    if (sortBy !== 'newest') params.set('sort', sortBy);
    
    // Construct URL properly based on page type
    const basePath = linkCode ? `/properties/link/${linkCode}` : '/properties';
    const queryString = params.toString();
    const newUrl = queryString ? `${basePath}?${queryString}` : basePath;
    
    // Only update URL if it's different from current URL
    if (window.location.pathname + window.location.search !== newUrl) {
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchTerm, selectedLocation, bedrooms, checkInDate, checkOutDate, selectedFeatures, page, pageSize, featuredOnly, sortBy, linkCode, priceRange, priceRangeEnabled]);

  const showNoProperties =
    !isLoading &&
    !isFetching &&
    !isLoadingProperties &&
    propertyList.length === 0;

  // Since availability is now handled on backend, we don't need to wait for separate availability check
  const isWaitingForAvailability = false;
  
  // Check if we're in a loading state
  const isAnyLoading = isLoading || isFetching || isLoadingProperties;

  // Update the header to show the correct count
  const headerText = useMemo(() => {
    const total = displayTotal;
    const baseText = `Discover ${total} luxury properties in Dubai`;
    
    // Only show availability text when BOTH dates are selected
    if (checkInDate && checkOutDate) {
      return `${baseText} (available ${formatDateString(checkInDate)} - ${formatDateString(checkOutDate)})`;
    }
    
    return baseText;
  }, [displayTotal, checkInDate, checkOutDate]);

  // Add a status message for date filtering
  const dateFilterStatus = useMemo(() => {
    if (checkInDate && checkOutDate) {
      return `Showing properties available from ${formatDateString(checkInDate)} to ${formatDateString(checkOutDate)}`;
    }
    return null;
  }, [checkInDate, checkOutDate]);

  if (isAuthLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
          <span className="ml-4 text-lg text-muted-foreground">Loading user...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="w-full py-8 md:py-12 px-4 md:px-8">
        <div className="w-full px-4 md:px-8">
          {/* Breadcrumbs */}
          <div className="flex items-center text-sm text-muted-foreground mb-8">
            <Link href="/">
              <span className="hover:text-primary cursor-pointer">Home</span>
            </Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span>Properties</span>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Error loading properties
                  </h3>
                  <div className="mt-1 text-sm text-red-700">
                    {error instanceof Error ? error.message : "An unknown error occurred"}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Header Section */}
          {!isLoading && (
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
              <div>
                <h1 className="text-3xl font-bold mb-2">Dubai Properties</h1>
                <p className="text-muted-foreground">
                  {headerText}
                </p>
                {dateFilterStatus && (
                  <p className="text-sm text-primary mt-1 font-medium">
                    📅 {dateFilterStatus}
                  </p>
                )}
              </div>

              {/* Sort dropdown */}
              <div className="flex items-center gap-2">
                <span className="text-sm">Sort by:</span>
                <Select value={sortBy} onValueChange={v => setSortBy(v as 'featured' | 'newest' | 'price-low' | 'price-high')}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="featured">Featured</SelectItem>
                    <SelectItem value="newest">Newest</SelectItem>
                    {canSeePrices && (
                      <>
                        <SelectItem value="price-low">Price: Low to High</SelectItem>
                        <SelectItem value="price-high">Price: High to Low</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
            {/* Active Filters Section */}
            {(searchTerm || selectedLocation || bedrooms !== null || checkInDate || checkOutDate || priceRangeEnabled || selectedFeatures.length > 0 || featuredOnly) && (
              <div className="bg-gray-50 border rounded-lg p-4 mb-6">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <h3 className="text-sm font-medium text-gray-700 mr-2">Active Filters:</h3>
                  
                  {/* Search Term Filter */}
                  {searchTerm && (
                    <div className="inline-flex items-center bg-blue-100 text-blue-800 text-sm rounded-full px-3 py-1 border border-blue-200">
                      <Search className="h-3 w-3 mr-1" />
                      Search: "{searchTerm}"
                      <button 
                        onClick={() => setSearchTerm('')} 
                        className="ml-2 text-blue-600 hover:text-blue-800"
                        title="Clear search"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}

                  {/* Location Filter */}
                  {selectedLocation && (
                    <div className="inline-flex items-center bg-green-100 text-green-800 text-sm rounded-full px-3 py-1 border border-green-200">
                      <MapPin className="h-3 w-3 mr-1" />
                      {DUBAI_AREAS.find(area => AREA_CODE_MAP[area] === selectedLocation) || selectedLocation}
                      <button 
                        onClick={() => setSelectedLocation(null)} 
                        className="ml-2 text-green-600 hover:text-green-800"
                        title="Clear location filter"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}

                  {/* Bedrooms Filter */}
                  {bedrooms !== null && (
                    <div className="inline-flex items-center bg-purple-100 text-purple-800 text-sm rounded-full px-3 py-1 border border-purple-200">
                      <Home className="h-3 w-3 mr-1" />
                      {bedrooms === 0 ? "Studio" : bedrooms >= 5 ? "5+ Bedrooms" : `${bedrooms} Bedroom${bedrooms === 1 ? "" : "s"}`}
                      <button 
                        onClick={() => setBedrooms(null)} 
                        className="ml-2 text-purple-600 hover:text-purple-800"
                        title="Clear bedrooms filter"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}

                  {/* Date Range Filter */}
                  {checkInDate && checkOutDate && (
                    <div className="inline-flex items-center bg-orange-100 text-orange-800 text-sm rounded-full px-3 py-1 border border-orange-200">
                      <Calendar className="h-3 w-3 mr-1" />
                      {formatDateString(checkInDate)} - {formatDateString(checkOutDate)}
                      <button 
                        onClick={() => {
                          setCheckInDate(undefined);
                          setCheckOutDate(undefined);
                        }} 
                        className="ml-2 text-orange-600 hover:text-orange-800"
                        title="Clear date filter"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}

                  {/* Partial Date Filter */}
                  {checkInDate && !checkOutDate && (
                    <div className="inline-flex items-center bg-yellow-100 text-yellow-800 text-sm rounded-full px-3 py-1 border border-yellow-200">
                      <Calendar className="h-3 w-3 mr-1" />
                      From {formatDateString(checkInDate)} (incomplete)
                      <button 
                        onClick={() => setCheckInDate(undefined)} 
                        className="ml-2 text-yellow-600 hover:text-yellow-800"
                        title="Clear check-in date"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}

                  {/* Price Range Filter */}
                  {priceRangeEnabled && canSeePrices && (
                    <div className="inline-flex items-center bg-indigo-100 text-indigo-800 text-sm rounded-full px-3 py-1 border border-indigo-200">
                      <DollarSign className="h-3 w-3 mr-1" />
                      AED {priceRange[0]} - {priceRange[1]}
                      <button 
                        onClick={() => setPriceRangeEnabled(false)} 
                        className="ml-2 text-indigo-600 hover:text-indigo-800"
                        title="Clear price filter"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}

                  {/* Featured Filter */}
                  {featuredOnly && (
                    <div className="inline-flex items-center bg-pink-100 text-pink-800 text-sm rounded-full px-3 py-1 border border-pink-200">
                      <Star className="h-3 w-3 mr-1" />
                      Featured Only
                      <button 
                        onClick={() => setFeaturedOnly(false)} 
                        className="ml-2 text-pink-600 hover:text-pink-800"
                        title="Clear featured filter"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}

                  {/* Features Filters */}
                  {selectedFeatures.map((feature) => (
                    <div key={feature} className="inline-flex items-center bg-teal-100 text-teal-800 text-sm rounded-full px-3 py-1 border border-teal-200">
                      {feature}
                      <button 
                        onClick={() => setSelectedFeatures(prev => prev.filter(f => f !== feature))} 
                        className="ml-2 text-teal-600 hover:text-teal-800"
                        title={`Remove ${feature} filter`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Clear All Filters Button */}
                <div className="flex justify-end">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={resetFilters}
                    className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Clear All Filters
                  </Button>
                </div>
              </div>
            )}

            {/* Mobile Filter Button */}
            <div className="md:hidden mb-4">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="w-full">
                    <Filter className="h-4 w-4 mr-2" /> Filters
                  </Button>
                </SheetTrigger>
                <SheetContent className="overflow-auto">
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                  </SheetHeader>
                  <div className="py-4 space-y-6">
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search properties..."
                        className="pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    {/* Location Filter */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">Location</label>
                      <Select
                        value={selectedLocation === null || selectedLocation === '' ? "any" : selectedLocation}
                        onValueChange={(value) => setSelectedLocation(value === "any" ? null : value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">All Locations</SelectItem>
                          {locations.map(location => (
                            <SelectItem key={location.value} value={location.value}>{location.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Featured Only Filter */}
                    <div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="featured-only-mobile"
                          checked={featuredOnly}
                          onCheckedChange={checked => setFeaturedOnly(!!checked)}
                        />
                        <label htmlFor="featured-only-mobile" className="text-sm">Show only featured properties</label>
                      </div>
                    </div>

                    {/* Bedrooms Filter */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">Bedrooms</label>
                      <Select
                        value={bedrooms === null ? "any" : bedrooms === 0 ? "studio" : bedrooms >= 5 ? "5plus" : bedrooms.toString()}
                        onValueChange={(value) => {
                          if (value === "any") {
                            setBedrooms(null);
                          } else if (value === "studio") {
                            setBedrooms(0);
                          } else if (value === "5plus") {
                            setBedrooms(5);
                          } else {
                            setBedrooms(parseInt(value));
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select bedrooms" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any Bedrooms</SelectItem>
                          <SelectItem value="studio">Studio</SelectItem>
                          <SelectItem value="1">1</SelectItem>
                          <SelectItem value="2">2</SelectItem>
                          <SelectItem value="3">3</SelectItem>
                          <SelectItem value="4">4</SelectItem>
                          <SelectItem value="5plus">5+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Date Selection */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">Dates</label>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal min-h-[40px] h-auto"
                        onClick={() => setMobileDateModalOpen(true)}
                      >
                        <Calendar className="mr-2 h-4 w-4 flex-shrink-0" />
                        <span className="truncate">
                          {checkInDate && checkOutDate ? (
                            <span className="block">
                              {formatDateString(checkInDate)} - {formatDateString(checkOutDate)}
                            </span>
                          ) : checkInDate ? (
                            <span>
                              {formatDateString(checkInDate)} - Select end date
                            </span>
                          ) : (
                            <span>Select dates</span>
                          )}
                        </span>
                      </Button>
                      {checkInDate && (
                        <p className="text-xs text-muted-foreground break-words text-primary mt-2">
                          <b>{formatDateString(checkInDate)}
                          {checkOutDate && ` to ${formatDateString(checkOutDate)}`}</b>
                        </p>
                      )}
                    </div>

                    {/* Price Range Filter - Mobile */}
                    {canSeePrices && properties.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <label className="text-sm font-medium">Price Range</label>
                          <Checkbox
                            id="enable-price-filter-mobile"
                            checked={priceRangeEnabled}
                            onCheckedChange={(checked) => setPriceRangeEnabled(!!checked)}
                          />
                        </div>
                        {priceRangeEnabled && (
                          <PriceRangeSlider
                            minPrice={priceRangeBounds.min}
                            maxPrice={priceRangeBounds.max}
                            value={priceRange}
                            onChange={setPriceRange}
                          />
                        )}
                      </div>
                    )}

                    {/* Features Filter */}
                    <div>
                      <label className="text-sm font-medium mb-4 block">Features</label>
                      <div className="space-y-2">
                        {allFeatures.map((feature) => (
                          <div className="flex items-center space-x-2" key={feature}>
                            <Checkbox
                              id={`${feature.toLowerCase().replace(/\s+/g, '-')}-mobile`}
                              checked={selectedFeatures.includes(feature)}
                              onCheckedChange={(checked) => handleFeatureChange(feature, !!checked)}
                            />
                            <label htmlFor={`${feature.toLowerCase().replace(/\s+/g, '-')}-mobile`} className="text-sm">
                              {feature}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Clear All Filters Button - Mobile */}
                    {(searchTerm || selectedLocation || bedrooms !== null || checkInDate || checkOutDate || priceRangeEnabled || selectedFeatures.length > 0) && (
                      <div>
                        <Button 
                          variant="outline" 
                          className="w-full" 
                          onClick={resetFilters}
                        >
                          <X className="h-4 w-4 mr-2" /> Clear All Filters
                        </Button>
                      </div>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>

          {!isLoading && (
            <div className="flex flex-col md:flex-row gap-8 w-full">
              {/* Filters - Desktop */}
              <div className="hidden md:block flex-shrink-0 w-[220px] space-y-6 sticky top-20 self-start h-fit">
                <div className="border rounded-lg p-6 space-y-6">
                  <div>
                    <div className="relative">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search properties..."
                        className="pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  <div >
                    <Select
                      value={selectedLocation === null || selectedLocation === '' ? "any" : selectedLocation}
                      onValueChange={(value) => setSelectedLocation(value === "any" ? null : value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">All Locations</SelectItem>
                        {locations.map(location => (
                          <SelectItem key={location.value} value={location.value}>{location.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div >
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="featured-only"
                        checked={featuredOnly}
                        onCheckedChange={checked => setFeaturedOnly(!!checked)}
                      />
                      <label htmlFor="featured-only" className="text-sm">Show only featured properties</label>
                    </div>
                  </div>

                  <div >
                    <Select
                      value={bedrooms === null ? "any" : bedrooms === 0 ? "studio" : bedrooms >= 5 ? "5plus" : bedrooms.toString()}
                      onValueChange={(value) => {
                        if (value === "any") {
                          setBedrooms(null);
                        } else if (value === "studio") {
                          setBedrooms(0);
                        } else if (value === "5plus") {
                          setBedrooms(5);
                        } else {
                          setBedrooms(parseInt(value));
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select bedrooms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any Bedrooms</SelectItem>
                        <SelectItem value="studio">Studio</SelectItem>
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                        <SelectItem value="3">3</SelectItem>
                        <SelectItem value="4">4</SelectItem>
                        <SelectItem value="5plus">5+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div >
                    <div className="space-y-4">
                      {isMobile() ? (
                        <>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal min-h-[40px] h-auto"
                            onClick={() => setMobileDateModalOpen(true)}
                          >
                            <Calendar className="mr-2 h-4 w-4 flex-shrink-0" />
                            <span className="truncate">
                              {checkInDate && checkOutDate ? (
                                <span className="block">
                                  {formatDateString(checkInDate)} - {formatDateString(checkOutDate)}
                                </span>
                              ) : checkInDate ? (
                                <span>
                                  {formatDateString(checkInDate)} - Select end date
                                </span>
                              ) : (
                                <span>Select dates</span>
                              )}
                            </span>
                          </Button>
                          <Dialog open={mobileDateModalOpen} onOpenChange={setMobileDateModalOpen}>
                            <DialogContent className="max-w-sm w-full p-0">
                              <div className="p-4">
                                <div className="flex justify-between items-center mb-3">
                                  <h4 className="font-medium">Select Dates</h4>
                                  {(checkInDate || checkOutDate) && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setCheckInDate(undefined);
                                        setCheckOutDate(undefined);
                                        setMobileDateModalOpen(false);
                                      }}
                                      className="text-xs h-6 px-2"
                                    >
                                      Clear
                                    </Button>
                                  )}
                                </div>
                                <DayPicker
                                  mode="range"
                                  selected={{ from: checkInDate, to: checkOutDate }}
                                  onSelect={(range) => {
                                    handleDateSelection(range);
                                    if (range?.from && range?.to) setMobileDateModalOpen(false);
                                  }}
                                  numberOfMonths={1}
                                  disabled={[{ before: today }]}
                                  className="border-0"
                                />
                              </div>
                            </DialogContent>
                          </Dialog>
                        </>
                      ) : (
                        <Popover open={calendarOpen} onOpenChange={setCalendarOpen} modal={false}>
                          <PopoverTrigger asChild>
                            <Button 
                              variant="outline" 
                              className="w-full justify-start text-left font-normal min-h-[40px] h-auto"
                            >
                              <Calendar className="mr-2 h-4 w-4 flex-shrink-0" />
                              <span className="truncate">
                                {checkInDate && checkOutDate ? (
                                  <span className="block">
                                    {formatDateString(checkInDate)} - {formatDateString(checkOutDate)}
                                  </span>
                                ) : checkInDate ? (
                                  <span>
                                    {formatDateString(checkInDate)} - Select end date
                                  </span>
                                ) : (
                                  <span>Select dates</span>
                                )}
                              </span>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <div className="p-3">
                              <div className="flex justify-between items-center mb-3">
                                <h4 className="font-medium">Select Dates</h4>
                                {(checkInDate || checkOutDate) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setCheckInDate(undefined);
                                      setCheckOutDate(undefined);
                                    }}
                                    className="text-xs h-6 px-2"
                                  >
                                    Clear
                                  </Button>
                                )}
                              </div>
                              <DayPicker
                                mode="range"
                                selected={{ 
                                  from: checkInDate, 
                                  to: checkOutDate 
                                }}
                                onSelect={handleDateSelection}
                                numberOfMonths={3}
                                disabled={[{ before: today }]}
                                className="border-0"
                              />
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                      {checkInDate && (
                        <p className="text-xs text-muted-foreground break-words text-primary">
                          <b>{formatDateString(checkInDate)}
                          {checkOutDate && ` to ${formatDateString(checkOutDate)}`}</b>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Price Range Filter - Desktop */}
                  {canSeePrices && properties.length > 0 && (
                    <div >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium">Price Range</h3>
                        <Checkbox
                          id="enable-price-filter"
                          checked={priceRangeEnabled}
                          onCheckedChange={(checked) => setPriceRangeEnabled(!!checked)}
                        />
                      </div>
                      {priceRangeEnabled && (
                        <PriceRangeSlider
                          minPrice={priceRangeBounds.min}
                          maxPrice={priceRangeBounds.max}
                          value={priceRange}
                          onChange={setPriceRange}
                        />
                      )}
                    </div>
                  )}

                  <div >
                    <h3 className="font-medium mb-4">Features</h3>
                    <div className="space-y-2">
                      {allFeatures.map((feature) => (
                        <div className="flex items-center space-x-2" key={feature}>
                          <Checkbox
                            id={feature.toLowerCase().replace(/\s+/g, '-')}
                            checked={selectedFeatures.includes(feature)}
                            onCheckedChange={(checked) => handleFeatureChange(feature, !!checked)}
                          />
                          <label htmlFor={feature.toLowerCase().replace(/\s+/g, '-')} className="text-sm">
                            {feature}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {(searchTerm || selectedLocation || bedrooms !== null || checkInDate || checkOutDate || priceRangeEnabled || selectedFeatures.length > 0) && (
                    <div>
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={resetFilters}
                      >
                        <X className="h-4 w-4 mr-2" /> Clear All Filters
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            

              {/* Property listing section */}
              <div className="flex-1 relative w-full max-w-none">
                <div className="relative min-h-[400px] w-full">
                  {isWaitingForAvailability ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-10">
                      <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                      <p className="text-lg text-muted-foreground">Checking availability...</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Finding properties available from {formatDateString(checkInDate)} to {formatDateString(checkOutDate)}
                      </p>
                    </div>
                  ) : isLoadingProperties ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-10">
                      <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                      <p className="text-lg text-muted-foreground">Loading properties...</p>
                    </div>
                  ) : displayProperties.length === 0 ? (
                    <div className="text-center py-12">
                      <h3 className="text-xl font-bold mb-2">No properties found</h3>
                      <p className="text-muted-foreground mb-4">
                        {checkInDate && checkOutDate 
                          ? `No properties available for your selected dates (${formatDateString(checkInDate)} - ${formatDateString(checkOutDate)}). Try different dates or adjust your filters.`
                          : "Try adjusting your filters or search criteria."
                        }
                      </p>
                      <Button onClick={resetFilters}>Reset Filters</Button>
                    </div>
                  ) : (
                    <>
                      <div className="grid gap-6 w-full" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                        {displayProperties.map((property) => (
                          <div key={property.id} className="border rounded-lg overflow-hidden group hover:shadow-md transition-all w-full">
                            {/* Mobile: Swipeable image carousel */}
                            <div className="relative aspect-[4/3] overflow-hidden md:hidden">
                              {/* Star icon for featured properties (mobile) */}
                              {property.featured && (
                                <div className="absolute top-2 right-2 z-30">
                                  <svg width="28" height="28" viewBox="0 0 24 24" fill="#FFD700" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                  </svg>
                                </div>
                              )}
                              <PropertyImageCarousel images={property.images ? [...property.images].sort((a, b) => (b.isPrimary === true ? 1 : 0) - (a.isPrimary === true ? 1 : 0)) : []} title={property.title} />
                            </div>
                            {/* Desktop: Single image */}
                            <div className="relative aspect-[4/3] overflow-hidden hidden md:block">
                              {/* Star icon for featured properties (desktop) */}
                              {property.featured && (
                                <div className="absolute top-2 right-2 z-30">
                                  <svg width="28" height="28" viewBox="0 0 24 24" fill="#FFD700" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                  </svg>
                                </div>
                              )}
                              {property.images && property.images.length > 0 ? (
                                <PropertyImageCarousel images={[...property.images].sort((a, b) => (b.isPrimary === true ? 1 : 0) - (a.isPrimary === true ? 1 : 0))} title={property.title} />
                              ) : (
                                <div className="w-full h-full bg-muted flex items-center justify-center">
                                  <p className="text-muted-foreground">No image available</p>
                                </div>
                              )}
                            </div>
                            <div className="p-4">
                              <h3 className="font-bold text-lg mb-1 truncate">{property.title}</h3>
                              <p className="text-muted-foreground text-sm flex items-center mb-2">
                                <MapPin className="w-4 h-4 mr-1" />
                                {property.area}
                              </p>
                              <div className="flex justify-between items-center mb-3">
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="flex items-center">
                                    <svg 
                                      className="w-4 h-4 mr-1" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24" 
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" 
                                      />
                                    </svg>
                                    {property.bedrooms === 0 ? "Studio" : `${property.bedrooms} Beds`}
                                  </span>
                                  <span className="flex items-center">
                                    <svg 
                                      className="w-4 h-4 mr-1" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24" 
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" 
                                      />
                                    </svg>
                                    {Number(property.bathrooms).toFixed(1)} Baths
                                  </span>
                                </div>
                                {(canSeePrices) ? (
                                  user === undefined ? (
                                    <div className="rounded px-2 py-1 text-sm bg-muted animate-pulse w-20 h-6" />
                                  ) : ((user && ['admin', 'manager', 'agent'].includes(user.role?.toLowerCase())) || !user || linkCode) ? (
                                    <div className="text-primary rounded px-2 py-1 text-sm font-semibold">
                                      AED {property.totalPrice || property.basePrice}
                                      <br />
                                      <span>/ night</span>
                                    </div>
                                  ) : (
                                    <div className="rounded px-2 py-1 text-sm">
                                      <Button
                                        variant="secondary"
                                        size="sm"
                                        className="text-xs p-2"
                                        aria-label="Check Availability"
                                        onClick={e => {
                                          e.preventDefault();
                                          window.location.href = `/contact-agent?propertyId=${property.id}`;
                                        }}
                                      >
                                        <Calendar className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  )
                                ) : null}
                              </div>
                              <div className="flex flex-wrap gap-1 mb-4">
                                {property.features && Array.isArray(property.features) && property.features.slice(0, 3).map((feature, index) => (
                                  <span 
                                    key={index} 
                                    className="inline-block text-xs bg-muted px-2 py-1 rounded"
                                  >
                                    {feature}
                                  </span>
                                ))}
                                {property.features && Array.isArray(property.features) && property.features.length > 3 && (
                                  <span className="inline-block text-xs bg-muted px-2 py-1 rounded">
                                    +{property.features.length - 3} more
                                  </span>
                                )}
                              </div>
                              <Link href={linkCode ? `/properties/${property.id}/link/${linkCode}` : `/properties/${property.id}`}>
                                <Button variant="outline" className="w-full" asChild>
                                  <span>View Details</span>
                                </Button>
                              </Link>
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Infinite scroll loader at the bottom */}
                      {propertyList.length < totalProperties && propertyList.length > 0 && (
                        <div ref={loadMoreRef} className="h-20 flex flex-col items-center justify-center mt-6 mb-4">
                          {isLoadingMore ? (
                            <>
                              <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                              <span className="text-base font-semibold text-primary">Loading more properties...</span>
                            </>
                          ) : (
                            <span className="text-base text-muted-foreground">Scroll down to load more</span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}